import {
  Grid,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Upload,
  notification,
  getValueFromEvent,
} from '@pankod/refine-antd';
import { file2Base64 } from '@pankod/refine-core';
import { EPaymentType } from 'enums';
import { ICategory } from 'interfaces';
import { useEffect, useState } from 'react';
import { getAllCompanyConfig } from 'services/config';
import { createExpense, ICreateExpenseReturnType } from 'services/expense';
import { getAllExpenseCategory } from 'services/expense-category';
import { getPaymentMethods } from 'services/payment';
import {
  beforeReceiptUploadValidation,
  isLessThanSize,
  isPurchaseReceiptFile,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import styles from './style.module.css';

interface IPaymentMethodOption {
  id: string;
  name: string;
}

type ExpenseDrawerProps = {
  title: string;
  visible: boolean;
  onClose: () => void;
  addExpenses: (expense: ICreateExpenseReturnType) => void;
};

const { Option } = Select;

export const ModuleExpenseDrawer: React.FC<ExpenseDrawerProps> = (props) => {
  const [isPaymentMethodDisable, setIsPaymentMethodDisable] = useState<boolean>(false);
  const [companyPaymentMethods, setCompanyPaymentMethods] = useState<IPaymentMethodOption[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();
  const paymentTypes = [EPaymentType.Cash, EPaymentType.Cheque];
  const [iseButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllCompanyConfig();
        const companyId = data[0].id;
        const [companyPaymentMethodsResponse, allExpenseCategoryResponse] = await Promise.all([
          getPaymentMethods('company', companyId),
          getAllExpenseCategory(),
        ]);

        const extendedCompanyPaymentMethods = companyPaymentMethodsResponse.map(
          (paymentMethod) => ({
            id: paymentMethod.id,
            name: paymentMethod.bankName,
          })
        );
        setCategories(allExpenseCategoryResponse.data);
        setCompanyPaymentMethods(extendedCompanyPaymentMethods);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
        setCompanyPaymentMethods([]);
      }
    })();
  }, []);

  const getOptions = (items: ICategory[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const getOptionsCompany = (items: IPaymentMethodOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onPaymentTypeSelect = (value: EPaymentType) => {
    form.resetFields(['paymentMethod', 'companyBank']);
    if (value === EPaymentType.Cheque) {
      setIsPaymentMethodDisable(false);
    } else {
      setIsPaymentMethodDisable(true);
    }
  };

  const onFinish = async (values: any) => {
    const { attachments } = values;
    const base64Files = [];
    if (attachments && attachments.length) {
      for (const file of attachments) {
        const format = file.type || '';
        const size = file.size || 0;
        if (isPurchaseReceiptFile(format) && isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
          if (file.originFileObj) {
            const base64String = await file2Base64(file);
            const { originFileObj, ...rest } = file;
            base64Files.push({ ...rest, base64String });
          } else {
            base64Files.push(file);
          }
        }
      }
    }

    const data: any = {
      category: values.category,
      paymentType: values.paymentType,
      paymentMethod: values.companyBank,
      note: values.note,
      expenseDate: new Date(),
      amount: values.amount,
      attachments: base64Files,
    };
    setIsButtonLoading(true);
    const createdExpense = await createExpense(data);
    props.addExpenses(createdExpense);
    setIsButtonLoading(false);
    props.onClose();
    form.resetFields();
  };

  return (
    <Drawer
      title={props.title}
      placement="right"
      width={breakpoint.sm ? '450px' : '100%'}
      onClose={props.onClose}
      visible={props.visible}
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Space direction="vertical" className={styles['file-upload']}>
          <Form.Item
            name="attachments"
            valuePropName="fileList"
            getValueFromEvent={getValueFromEvent}
            rules={[
              {
                required: false,
                message: 'Attachment is required',
              },
            ]}
          >
            <Upload.Dragger
              listType="picture"
              accept="image/jpeg, image/png, image/heic, image/heif, .heic, .heif, application/pdf"
              multiple
              maxCount={2}
              beforeUpload={beforeReceiptUploadValidation}
            >
              <p className="ant-upload-text">Drag & Drop</p>
              <p className="ant-upload-text">Image or PDF files in this area</p>
              <p className="ant-upload-hint">
                {`Files must be less than ${MAX_UPLOAD_FILE_SIZE_MB}MB and maximum file count is 2`}
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Space>
        <Form.Item
          label="Expense Category"
          name="category"
          rules={[{ required: true, message: 'Please select a category' }]}
        >
          <Select
            style={{ width: '100%' }}
            placeholder="Select Category"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {getOptions(categories)}
          </Select>
        </Form.Item>
        <Form.Item
          label="Payment Type"
          name="paymentType"
          rules={[{ required: true, message: 'Please select a type' }]}
        >
          <Select
            showSearch
            placeholder="Select a type"
            onSelect={onPaymentTypeSelect}
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {paymentTypes.map((item) => (
              <Option key={item} value={item}>
                {item}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Payment Method"
          name="companyBank"
          rules={[
            {
              required: true && !isPaymentMethodDisable,
              message: 'Please select a method',
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Select a method"
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            disabled={isPaymentMethodDisable}
          >
            {getOptionsCompany(companyPaymentMethods)}
          </Select>
        </Form.Item>

        <Form.Item label="Note" name="note">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item
          label="Amount"
          name="amount"
          rules={[
            {
              required: true,
              type: 'integer',
              message: 'Non Fractional Input Only',
            },
          ]}
        >
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
          <Button type="primary" htmlType="submit" loading={iseButtonLoading}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
